import { makeStyles, shorthands } from "@fluentui/react-components";
import { BRAND } from "../risikoanalyseStep/tokens";

/**
 * Shared visual primitives for the Immobilie contract step form.
 * Mirrors the look of risikoImmobilieStep components: white cards with dashed
 * borders, purple/gold tile-pickers, focus rings on inputs.
 */
export const useSharedStyles = makeStyles({
  card: {
    backgroundColor: BRAND.white,
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
    ...shorthands.borderRadius("10px"),
    paddingTop: "20px",
    paddingBottom: "20px",
    paddingLeft: "20px",
    paddingRight: "20px",
    display: "flex",
    flexDirection: "column",
    rowGap: "14px",
  },
  cardSection: {
    display: "flex",
    flexDirection: "column",
    rowGap: "12px",
  },
  label: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.72rem",
    fontWeight: 600,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    color: "#5d5664",
  },
  field: {
    display: "flex",
    flexDirection: "column",
    rowGap: "4px",
  },
  input: {
    width: "100%",
    boxSizing: "border-box",
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.95rem",
    color: BRAND.ink,
    backgroundColor: BRAND.white,
    paddingTop: "9px",
    paddingBottom: "9px",
    paddingLeft: "12px",
    paddingRight: "12px",
    ...shorthands.borderRadius("6px"),
    ...shorthands.border("1px", "solid", "#d4cfd9"),
    outlineWidth: 0,
    transitionProperty: "border-color, box-shadow",
    transitionDuration: "120ms",
    ":focus": {
      ...shorthands.borderColor(BRAND.purple),
      boxShadow: `0 0 0 3px ${BRAND.purple}33`,
    },
  },
  textarea: {
    width: "100%",
    boxSizing: "border-box",
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.95rem",
    color: BRAND.ink,
    backgroundColor: BRAND.white,
    paddingTop: "9px",
    paddingBottom: "9px",
    paddingLeft: "12px",
    paddingRight: "12px",
    minHeight: "90px",
    resize: "vertical",
    ...shorthands.borderRadius("6px"),
    ...shorthands.border("1px", "solid", "#d4cfd9"),
    outlineWidth: 0,
    ":focus": {
      ...shorthands.borderColor(BRAND.purple),
      boxShadow: `0 0 0 3px ${BRAND.purple}33`,
    },
  },
  helper: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.78rem",
    color: BRAND.muted,
  },
  tileGrid: {
    display: "grid",
    gridTemplateColumns: "1fr",
    gap: "12px",
    "@media (min-width: 540px)": {
      gridTemplateColumns: "1fr 1fr",
    },
    "@media (min-width: 880px)": {
      gridTemplateColumns: "repeat(4, 1fr)",
    },
  },
  tileGrid2: {
    display: "grid",
    gridTemplateColumns: "1fr",
    gap: "12px",
    "@media (min-width: 540px)": {
      gridTemplateColumns: "1fr 1fr",
    },
  },
  tileGrid3: {
    display: "grid",
    gridTemplateColumns: "1fr",
    gap: "12px",
    "@media (min-width: 540px)": {
      gridTemplateColumns: "1fr 1fr",
    },
    "@media (min-width: 880px)": {
      gridTemplateColumns: "repeat(3, 1fr)",
    },
  },
  tile: {
    cursor: "pointer",
    appearance: "none",
    textAlign: "left",
    backgroundColor: BRAND.white,
    backgroundImage: "none",
    color: BRAND.ink,
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.95rem",
    paddingTop: "14px",
    paddingBottom: "14px",
    paddingLeft: "16px",
    paddingRight: "16px",
    ...shorthands.borderRadius("10px"),
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
    transitionProperty: "border-color, background-color, box-shadow",
    transitionDuration: "150ms",
    display: "flex",
    flexDirection: "column",
    rowGap: "8px",
    minHeight: "90px",
    ":hover": {
      ...shorthands.borderColor(BRAND.purple),
      backgroundColor: "#f5eef6",
    },
    ":disabled": {
      cursor: "not-allowed",
      opacity: 0.45,
    },
  },
  tileActive: {
    ...shorthands.border("2px", "dashed", BRAND.purple),
    backgroundColor: "#f5eef6",
    boxShadow: "0 4px 14px rgba(77,32,81,0.18)",
  },
  tileTitle: {
    fontWeight: 700,
    fontSize: "1rem",
    color: BRAND.ink,
  },
  tileHint: {
    fontSize: "0.82rem",
    color: BRAND.muted,
  },
  twoCol: {
    display: "grid",
    gridTemplateColumns: "1fr",
    gap: "12px",
    "@media (min-width: 720px)": {
      gridTemplateColumns: "1fr 1fr",
    },
  },
  threeCol: {
    display: "grid",
    gridTemplateColumns: "1fr",
    gap: "12px",
    "@media (min-width: 720px)": {
      gridTemplateColumns: "repeat(3, 1fr)",
    },
  },
  badge: {
    display: "inline-flex",
    alignItems: "center",
    columnGap: "6px",
    paddingTop: "4px",
    paddingBottom: "4px",
    paddingLeft: "10px",
    paddingRight: "10px",
    fontSize: "0.72rem",
    fontWeight: 700,
    letterSpacing: "0.04em",
    textTransform: "uppercase",
    backgroundColor: BRAND.purple,
    color: BRAND.white,
    ...shorthands.borderRadius("999px"),
  },
  pillRow: {
    display: "flex",
    flexWrap: "wrap",
    columnGap: "8px",
    rowGap: "8px",
    alignItems: "center",
  },
  pillBtn: {
    appearance: "none",
    cursor: "pointer",
    backgroundColor: BRAND.white,
    backgroundImage: "none",
    color: BRAND.ink,
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.85rem",
    fontWeight: 700,
    paddingTop: "6px",
    paddingBottom: "6px",
    paddingLeft: "16px",
    paddingRight: "16px",
    minWidth: "64px",
    ...shorthands.borderRadius("999px"),
    ...shorthands.border("1px", "solid", "#d4cfd9"),
    transitionProperty: "background-color, border-color, color",
    transitionDuration: "120ms",
    ":hover": {
      ...shorthands.borderColor(BRAND.purple),
    },
  },
  pillBtnActive: {
    backgroundColor: BRAND.purple,
    color: BRAND.white,
    ...shorthands.borderColor(BRAND.purple),
  },
  inlineSuffix: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.85rem",
    color: BRAND.muted,
    fontWeight: 600,
  },
  perGefahrRow: {
    display: "grid",
    gridTemplateColumns: "1fr",
    gap: "8px",
    paddingTop: "10px",
    paddingBottom: "10px",
    ...shorthands.borderBottom("1px", "dashed", BRAND.borderDashed),
    "@media (min-width: 880px)": {
      gridTemplateColumns: "1.6fr 1.4fr 1fr",
      alignItems: "center",
    },
  },
  perGefahrTitle: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.9rem",
    fontWeight: 700,
    color: BRAND.ink,
  },
  inputWithSuffix: {
    display: "flex",
    alignItems: "center",
    columnGap: "8px",
  },
});
